<template>
  <Teleport to="body">
    <div v-if="open" class="feedback-overlay" @click.self="close">
      <div class="feedback-modal" :class="{ 'dark-mode': isDarkMode }">
        <!-- Success State -->
        <div v-if="submitted" class="success-state">
          <div class="success-icon">✓</div>
          <h3>Thank You!</h3>
          <p>Your feedback has been submitted successfully.</p>
          <button class="btn btn-primary btn-sm" @click="close">Close</button>
        </div>

        <!-- Form State -->
        <div v-else>
          <div class="modal-header">
            <h4>Send Feedback</h4>
            <button class="close-btn" @click="close" aria-label="Close">&times;</button>
          </div>

          <form @submit.prevent="submitFeedback">
            <!-- Context Info Banner -->
            <div class="context-banner">
              <div class="context-item">
                <span class="context-label">Page:</span>
                <span class="context-value">{{ context.route_name }}</span>
              </div>
              <div class="context-item">
                <span class="context-label">Device:</span>
                <span class="context-value">{{ context.device_type }}</span>
              </div>
            </div>

            <!-- Feedback Type Selection -->
            <div class="form-group">
              <label class="form-label">What type of feedback?</label>
              <div class="feedback-types">
                <button
                  v-for="ft in feedbackTypes"
                  :key="ft.value"
                  type="button"
                  class="feedback-type-btn"
                  :class="{ active: form.feedback_type === ft.value }"
                  @click="form.feedback_type = ft.value"
                >
                  <span class="type-icon">{{ ft.icon }}</span>
                  <span class="type-label">{{ ft.label }}</span>
                </button>
              </div>
            </div>

            <!-- Title -->
            <div class="form-group">
              <label class="form-label">Brief summary <span class="required">*</span></label>
              <input
                v-model="form.title"
                type="text"
                class="form-control form-control-sm"
                placeholder="e.g., Button doesn't work, Need feature X..."
                maxlength="255"
                required
              />
            </div>

            <!-- Description -->
            <div class="form-group">
              <label class="form-label">Details (optional)</label>
              <textarea
                v-model="form.description"
                class="form-control form-control-sm"
                rows="3"
                placeholder="Tell us more... What happened? What did you expect?"
              ></textarea>
            </div>

            <!-- Screenshot Upload -->
            <div class="form-group">
              <label class="form-label">Screenshots (optional)</label>
              <div class="screenshot-upload">
                <input
                  type="file"
                  ref="cameraInput"
                  @change="onFilesSelected"
                  accept="image/*,video/*"
                  capture
                  hidden
                />
                <input
                  type="file"
                  ref="fileInput"
                  @change="onFilesSelected"
                  accept="image/*,video/*"
                  multiple
                  hidden
                />
                <div class="upload-buttons">
                  <button 
                    type="button" 
                    class="add-screenshot-btn" 
                    @click="triggerCameraInput"
                    :disabled="selectedFiles.length >= 3"
                  >
                    <span class="add-icon">📷</span>
                    <span>Camera</span>
                  </button>
                  <button 
                    type="button" 
                    class="add-screenshot-btn" 
                    @click="triggerFileInput"
                    :disabled="selectedFiles.length >= 3"
                  >
                    <span class="add-icon">🖼️</span>
                    <span>Files</span>
                  </button>
                </div>
                <div v-if="selectedFiles.length" class="selected-files">
                  <div 
                    v-for="(file, idx) in selectedFiles" 
                    :key="idx" 
                    class="file-preview"
                  >
                    <video v-if="file.isVideo" :src="file.preview" muted class="preview-media"></video>
                    <img v-else :src="file.preview" alt="Preview" class="preview-media" />
                    <button 
                      type="button" 
                      @click="removeFile(idx)" 
                      class="remove-btn"
                      title="Remove"
                    >&times;</button>
                  </div>
                </div>
                <p class="upload-hint">Max 3 files, 10MB images / 25MB videos</p>
              </div>
            </div>

            <!-- Category -->
            <div class="form-group">
              <label class="form-label">Category</label>
              <select v-model="form.category" class="form-select form-select-sm">
                <option value="">Auto-detect from page</option>
                <option value="dashboard">Dashboard</option>
                <option value="admin">Admin</option>
                <option value="feedback">Feedback</option>
                <option value="login">Login / Authentication</option>
                <option value="reports">Reports</option>
                <option value="analytics">Analytics</option>
                <option value="other">Other</option>
              </select>
            </div>

            <!-- Technical Details (Collapsible) -->
            <details class="tech-details">
              <summary>Technical details (auto-captured)</summary>
              <pre>{{ technicalContext }}</pre>
            </details>

            <!-- Error Message -->
            <div v-if="error" class="alert alert-danger mt-3">
              {{ error }}
            </div>

            <!-- Actions -->
            <div class="modal-actions">
              <button type="button" class="btn btn-outline-secondary btn-sm" @click="close">Cancel</button>
              <button
                type="submit"
                class="btn btn-primary btn-sm"
                :disabled="submitting || !isValid"
              >
                <span v-if="submitting">{{ uploadProgress }}</span>
                <span v-else>Send Feedback</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { ref, reactive, computed, watch, inject } from 'vue';
import { useRoute } from 'vue-router';
import http from '@/api/http';

export default {
  name: 'FeedbackModal',
  props: {
    open: {
      type: Boolean,
      default: false
    }
  },
  emits: ['close'],
  setup(props, { emit }) {
    const route = useRoute();
    const isDarkMode = inject('isDarkMode', ref(false));

    const feedbackTypes = [
      { value: 'bug', icon: '🐛', label: 'Bug' },
      { value: 'feature', icon: '💡', label: 'Feature' },
      { value: 'confusion', icon: '🤔', label: 'Confused' },
      { value: 'praise', icon: '👍', label: 'Praise' }
    ];

    const form = reactive({
      feedback_type: 'bug',
      title: '',
      description: '',
      category: ''
    });

    const context = reactive({
      route_path: '',
      route_name: '',
      screen_width: 0,
      screen_height: 0,
      user_agent: '',
      location_id: null,
      device_type: '',
      scroll_y: 0,
      timestamp: ''
    });

    const submitting = ref(false);
    const submitted = ref(false);
    const error = ref('');
    const uploadProgress = ref('Sending...');
    
    // Screenshot handling
    const fileInput = ref(null);
    const cameraInput = ref(null);
    const selectedFiles = ref([]);

    // Route name mapping
    const routeNameMap = {
      '/': 'Landing Page',
      '/dashboard': 'Dashboard',
      '/home': 'Home',
      '/login': 'Login',
      '/admin/users': 'Users Admin',
      '/admin/locations': 'Locations Admin',
      '/admin/roles': 'Roles Admin',
      '/admin/queries': 'Queries Admin',
      '/admin/email-templates': 'Email Templates',
      '/admin/prompts': 'AI Prompts',
      '/admin/changelog': 'Changelog',
      '/admin/analytics': 'GA4 Analytics',
      '/admin/soci-analytics': 'SOCi Analytics',
      '/admin/meta-analytics': 'Meta Analytics',
      '/admin/sentiment': 'Sentiment Dashboard',
      '/admin/pam-analytics': 'PAM Analytics',
      '/admin/user-feedback': 'User Feedback Admin'
    };

    const getDeviceType = () => {
      const width = window.innerWidth;
      if (width < 480) return 'Mobile (small)';
      if (width < 768) return 'Mobile';
      if (width < 1024) return 'Tablet';
      return 'Desktop';
    };

    const captureContext = () => {
      context.route_path = route.path;
      context.route_name = routeNameMap[route.path] || route.name || route.path;
      context.screen_width = window.innerWidth;
      context.screen_height = window.innerHeight;
      context.user_agent = navigator.userAgent;
      context.device_type = getDeviceType();
      context.scroll_y = Math.round(window.scrollY);
      context.timestamp = new Date().toISOString();
      context.location_id = route.query?.location_id || route.params?.location_id || localStorage.getItem('lastLocationId') || null;
    };

    const technicalContext = computed(() => {
      return JSON.stringify({
        page: context.route_path,
        viewport: `${context.screen_width}x${context.screen_height}`,
        device: context.device_type,
        scrollY: context.scroll_y,
        userAgent: context.user_agent.substring(0, 100),
        timestamp: context.timestamp,
        locationId: context.location_id
      }, null, 2);
    });

    const isValid = computed(() => {
      return form.title.length >= 3;
    });

    // Screenshot methods
    const triggerCameraInput = () => {
      cameraInput.value?.click();
    };

    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const getVideoDuration = (file) => {
      return new Promise((resolve) => {
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.onloadedmetadata = () => {
          URL.revokeObjectURL(video.src);
          resolve(video.duration);
        };
        video.onerror = () => resolve(0);
        video.src = URL.createObjectURL(file);
      });
    };

    const onFilesSelected = async (event) => {
      const files = Array.from(event.target.files);
      for (const file of files) {
        if (selectedFiles.value.length >= 3) break;
        
        const isVideo = file.type.startsWith('video/');
        const isImage = file.type.startsWith('image/');
        if (!isVideo && !isImage) continue;
        
        // Size limits: 25MB for video, 10MB for images
        const maxSize = isVideo ? 25 * 1024 * 1024 : 10 * 1024 * 1024;
        if (file.size > maxSize) {
          alert(isVideo ? 'Video must be under 25MB' : 'Image must be under 10MB');
          continue;
        }
        
        // Check video duration (max 30 seconds)
        if (isVideo) {
          const duration = await getVideoDuration(file);
          if (duration > 30) {
            alert('Video must be 30 seconds or less');
            continue;
          }
        }

        selectedFiles.value.push({
          file,
          preview: URL.createObjectURL(file),
          isVideo
        });
      }
      event.target.value = '';
    };

    const removeFile = (idx) => {
      URL.revokeObjectURL(selectedFiles.value[idx].preview);
      selectedFiles.value.splice(idx, 1);
    };

    const uploadScreenshots = async (feedbackId) => {
      for (let i = 0; i < selectedFiles.value.length; i++) {
        uploadProgress.value = `Uploading ${i + 1}/${selectedFiles.value.length}...`;
        const { file } = selectedFiles.value[i];
        const formData = new FormData();
        formData.append('file', file);
        try {
          await http.post(`/api/v1/feedback-attachments/${feedbackId}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (err) {
          console.error('Failed to upload screenshot:', err);
        }
      }
    };

    const resetForm = () => {
      form.feedback_type = 'bug';
      form.title = '';
      form.description = '';
      form.category = '';
      error.value = '';
      submitted.value = false;
      submitting.value = false;
      uploadProgress.value = 'Sending...';
      // Clean up file previews
      selectedFiles.value.forEach(f => URL.revokeObjectURL(f.preview));
      selectedFiles.value = [];
    };

    const close = () => {
      setTimeout(() => {
        resetForm();
      }, 300);
      emit('close');
    };

    const submitFeedback = async () => {
      if (!isValid.value) return;

      submitting.value = true;
      error.value = '';
      uploadProgress.value = 'Sending...';

      try {
        let category = form.category;
        if (!category) {
          if (route.path.startsWith('/admin')) {
            category = 'admin';
          } else if (route.path.startsWith('/dashboard')) {
            category = 'dashboard';
          } else if (route.path === '/login') {
            category = 'login';
          } else {
            category = 'other';
          }
        }

        const contextParts = [];
        contextParts.push(`Page: ${context.route_name}`);
        contextParts.push(`Device: ${context.device_type}`);
        contextParts.push(`Viewport: ${context.screen_width}x${context.screen_height}`);
        contextParts.push(`URL: ${context.route_path}`);
        contextParts.push(`Scroll: ${context.scroll_y}px`);
        contextParts.push(`Time: ${context.timestamp}`);

        const fullDescription = [
          form.description,
          '',
          '--- Auto-captured Context ---',
          contextParts.join('\n')
        ].filter(Boolean).join('\n');

        const payload = {
          feedback_type: form.feedback_type,
          title: form.title,
          description: fullDescription,
          category: category,
          route_path: context.route_path,
          route_name: context.route_name,
          user_agent: context.user_agent,
          screen_width: context.screen_width,
          screen_height: context.screen_height,
          location_id: context.location_id ? parseInt(context.location_id) : null
        };

        const response = await http.post('/api/v1/user-feedback', payload);
        
        // Upload screenshots if any
        if (selectedFiles.value.length > 0 && response.data?.id) {
          await uploadScreenshots(response.data.id);
        }
        
        submitted.value = true;

        // Auto-close after showing success message
        setTimeout(() => {
          close();
        }, 1500);
      } catch (err) {
        console.error('Failed to submit feedback:', err);
        error.value = err.response?.data?.detail || 'Failed to submit feedback. Please try again.';
      } finally {
        submitting.value = false;
      }
    };

    watch(() => props.open, (newVal) => {
      if (newVal) {
        captureContext();
      }
    });

    return {
      isDarkMode,
      feedbackTypes,
      form,
      context,
      technicalContext,
      submitting,
      submitted,
      error,
      isValid,
      close,
      submitFeedback,
      uploadProgress,
      fileInput,
      cameraInput,
      selectedFiles,
      triggerFileInput,
      triggerCameraInput,
      onFilesSelected,
      removeFile
    };
  }
};
</script>

<style scoped>
.feedback-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

.feedback-modal {
  background: #fff;
  border-radius: 12px;
  padding: 1.5rem;
  width: 90%;
  max-width: 480px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: slideUp 0.3s ease;
}

@keyframes slideUp {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

.feedback-modal.dark-mode {
  background: #1a1d23;
  color: #e4e6eb;
}

.modal-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.dark-mode .modal-header { border-bottom-color: #2d323a; }

.modal-header h4 { margin: 0; font-size: 1.1rem; font-weight: 600; }

.close-btn {
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6c757d;
  padding: 0;
  line-height: 1;
}

.close-btn:hover { color: #dc3545; }

.context-banner {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.75rem;
  background: #f8f9fa;
  border-radius: 8px;
}

.dark-mode .context-banner { background: #16181d; }

.context-banner .context-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  font-size: 0.8rem;
}

.context-banner .context-label { color: #6c757d; }
.dark-mode .context-banner .context-label { color: #9ca3af; }
.context-banner .context-value { font-weight: 500; }

.form-group { margin-bottom: 1rem; }

.form-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  font-size: 0.85rem;
}

.required { color: #dc3545; }

.feedback-types {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
}

.feedback-type-btn {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
}

.dark-mode .feedback-type-btn { border-color: #2d323a; color: #e4e6eb; }

.feedback-type-btn:hover { border-color: #17a2b8; background: rgba(23, 162, 184, 0.05); }
.feedback-type-btn.active { border-color: #17a2b8; background: rgba(23, 162, 184, 0.1); }

.type-icon { font-size: 1.5rem; margin-bottom: 0.25rem; }
.type-label { font-size: 0.75rem; font-weight: 500; }
.feedback-type-btn.active .type-label { color: #17a2b8; }

.form-control, .form-select { width: 100%; }

.dark-mode .form-control, .dark-mode .form-select {
  background-color: #16181d;
  border-color: #2d323a;
  color: #e4e6eb;
}

.dark-mode .form-control::placeholder { color: #6b7280; opacity: 1; }
.dark-mode .form-select option { background-color: #16181d; color: #e4e6eb; }

/* Screenshot Upload */
.screenshot-upload { display: flex; flex-direction: column; gap: 0.5rem; }

.upload-buttons { display: grid; grid-template-columns: 1fr 1fr; gap: 0.5rem; }

.add-screenshot-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 0.625rem;
  background: #f8f9fa;
  border: 2px dashed #dee2e6;
  border-radius: 8px;
  color: #6c757d;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 0.85rem;
}

.dark-mode .add-screenshot-btn { background: #16181d; border-color: #2d323a; color: #9ca3af; }

.add-screenshot-btn:hover:not(:disabled) { border-color: #17a2b8; color: #17a2b8; }
.add-screenshot-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.add-icon { font-size: 1rem; }

.selected-files { display: flex; gap: 0.5rem; flex-wrap: wrap; }

.file-preview {
  position: relative;
  width: 60px;
  height: 60px;
  border-radius: 6px;
  overflow: hidden;
  border: 2px solid #dee2e6;
}

.dark-mode .file-preview { border-color: #2d323a; }

.preview-media { width: 100%; height: 100%; object-fit: cover; }

.file-preview .remove-btn {
  position: absolute;
  top: 2px;
  right: 2px;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: rgba(220, 53, 69, 0.9);
  color: white;
  border: none;
  cursor: pointer;
  font-size: 12px;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
}

.file-preview .remove-btn:hover { background: #dc3545; }

.upload-hint { font-size: 0.7rem; color: #6c757d; margin: 0; }
.dark-mode .upload-hint { color: #9ca3af; }

.tech-details { margin-top: 1rem; font-size: 0.75rem; color: #6c757d; }
.dark-mode .tech-details { color: #9ca3af; }

.tech-details summary {
  cursor: pointer;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
}

.dark-mode .tech-details summary { background: #16181d; }

.tech-details pre {
  margin: 0.5rem 0 0;
  padding: 0.5rem;
  background: #f8f9fa;
  border-radius: 4px;
  overflow-x: auto;
  font-size: 0.7rem;
  white-space: pre-wrap;
  word-break: break-all;
}

.dark-mode .tech-details pre { background: #16181d; }

.modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  margin-top: 1.5rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.dark-mode .modal-actions { border-top-color: #2d323a; }

.success-state { text-align: center; padding: 2rem 1rem; }

.success-icon {
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: #198754;
  color: white;
  font-size: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 1rem;
}

.success-state h3 { margin: 0 0 0.5rem; }
.success-state p { color: #6c757d; margin-bottom: 1.5rem; }
.dark-mode .success-state p { color: #9ca3af; }

.alert-danger {
  padding: 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  background: rgba(220, 53, 69, 0.1);
  border: 1px solid rgba(220, 53, 69, 0.3);
  color: #dc3545;
}

@media (max-width: 480px) {
  .feedback-types { grid-template-columns: repeat(4, 1fr); }
  .type-label { font-size: 0.65rem !important; }
}
</style>
